import {useMutation, useQuery, useQueryClient} from "react-query";
import useFeedback from "../components/feedback/useFeedback";
import {CONECTOR_ESFINGE_URL} from "../shared/env";
import createApi, {createQueryFetcher} from "./api";
import {resumirOcorrenciaFolha} from "../shared/resumoFolhaCompetencia";

export const api = createApi(CONECTOR_ESFINGE_URL);
const queryFetcher = createQueryFetcher(api);
const REFRESH_INTERVAL = 10000;
const PAGE_SIZE = 25;
const INTEGRACAO_ATOS_PESSOAL_URL = "api/v1/integracao/folha/atos-pessoal";
const INTEGRACAO_QUADRO_VAGAS_URL = "api/v1/integracao/folha/quadroVagas";
const INTEGRACAO_FINANCEIRA_URL = "api/v1/integracao/financeira";
const SERVICOS_GERAIS_URL = "api/v1/servicosGerais/consultar";
const INTEGRACAO_ENTREGA_URL = "api/v1/integracao/entrega";
const DASHBOARD_ENVIOS_URL = "api/v1/dashboard/envios";

export function useDashboardEnvios(
    {
        dataInicial,
        dataFinal,
        competencia,
        modulos = [],
        destinos = [],
        incluirArquivados = false
    },
    options = {}
) {
    const url = withQueryParams(DASHBOARD_ENVIOS_URL, {
        dataInicial: competencia ? undefined : dataInicial,
        dataFinal: competencia ? undefined : dataFinal,
        competencia,
        modulos,
        destinos,
        incluirArquivados
    });

    return useQuery([url], queryFetcher, {
        ...options,
        keepPreviousData: false,
        refetchInterval: REFRESH_INTERVAL
    });
}

export async function consultarFolhasPreparadas() {
    const resumos = [];
    let page = 0;
    let totalPages;
    do {
        const {data} = await api.get("api/v1/ocorrencias/paginado", {
            params: {
                page,
                size: 100,
                tipos: "FOLHA_PAGAMENTO_INICIAR",
                somenteRaizes: true,
                incluirArquivados: true
            }
        });
        if (
            !Array.isArray(data?.pagina?.content) ||
            !Number.isInteger(data.pagina.totalPages)
        ) {
            throw new Error(
                "Resposta incompleta ao consultar as folhas preparadas."
            );
        }
        data.pagina.content.forEach((ocorrencia) => {
            const resumo = resumirOcorrenciaFolha(ocorrencia);
            if (resumo) resumos.push(resumo);
        });
        totalPages = data.pagina.totalPages;
        page++;
    } while (page < totalPages);
    return resumos;
}

export function useFolhasPreparadas() {
    return useQuery(
        ["folhas-preparadas-por-competencia"],
        consultarFolhasPreparadas,
        {
            staleTime: 30000,
            refetchInterval: 30000
        }
    );
}

export function useOcorrenciasPaginado(
    page = 0,
    estados = [],
    expressao = "",
    tipo = "",
    incluirArquivados = false,
    destinoEntrega = ""
) {
    const query = `api/v1/ocorrencias/paginado/resumo?page=${page}&size=${PAGE_SIZE}&estados=${estados.join()}&expressao=${encodeURIComponent(
        expressao
    )}&tipos=${tipo}&incluirArquivados=${incluirArquivados}&somenteRaizes=true${
        destinoEntrega
            ? `&destinoEntrega=${encodeURIComponent(destinoEntrega)}`
            : ""
    }`;
    return useQuery(query, queryFetcher, {
        refetchInterval: REFRESH_INTERVAL,
        keepPreviousData: true
    });
}

export function useDetalheOcorrencia(id, options = {}) {
    return useQuery(`api/v1/ocorrencias/${id}`, queryFetcher, {
        refetchInterval: REFRESH_INTERVAL,
        ...options
    });
}

export function useConectorStatus(options = {}) {
    return useQuery(`/actuator/health`, queryFetcher, options);
}

export function useConfiguracaoEntrega(options = {}) {
    return useQuery(`${INTEGRACAO_ENTREGA_URL}/configuracao`, queryFetcher, {
        staleTime: 30000,
        refetchInterval: 30000,
        ...options
    });
}

export function useReconciliarEntregaFornecedor() {
    const queryClient = useQueryClient();

    return useMutation(
        ({
            ocorrenciaId,
            acao,
            justificativa,
            protocoloFornecedor,
            codigoRegistroInformacao,
            evidenciaTceMs,
            hashPayload
        }) =>
            api.post(
                `${INTEGRACAO_ENTREGA_URL}/fornecedor/ocorrencias/${ocorrenciaId}/reconciliacoes`,
                {
                    acao,
                    justificativa,
                    protocoloFornecedor,
                    codigoRegistroInformacao,
                    evidenciaTceMs,
                    hashPayload
                }
            ),
        {
            onSuccess: () => queryClient.invalidateQueries()
        }
    );
}

export function useConsultarServicoGeral() {
    return useMutation(({recurso, parametros = {}}) =>
        api
            .get(`${SERVICOS_GERAIS_URL}/${recurso}`, {params: parametros})
            .then((response) => response.data)
    );
}

export function usePlanoAtosPessoal(
    anoMes,
    {
        incluirInativos = false,
        incluirPensionistas = false,
        incluirConcursos = false
    } = {}
) {
    const url = withQueryParams(
        `${INTEGRACAO_ATOS_PESSOAL_URL}/plano/${anoMes}`,
        {incluirInativos, incluirPensionistas, incluirConcursos}
    );

    return useQuery([url], queryFetcher, {
        enabled: isAnoMesValido(anoMes),
        keepPreviousData: true
    });
}

export function useSituacaoCargaInicial(anoMes, enabled = true) {
    return useQuery(
        [INTEGRACAO_ATOS_PESSOAL_URL + "/carga-inicial/situacao/" + anoMes],
        queryFetcher,
        {
            enabled: enabled && isAnoMesValido(anoMes),
            staleTime: 30000,
            refetchInterval: 60000
        }
    );
}

export function useSimularAtosPessoal() {
    return useIntegracaoMutation(
        (payload) =>
            api.post(`${INTEGRACAO_ATOS_PESSOAL_URL}/simular`, payload),
        "Simulação de Atos de Pessoal realizada com sucesso"
    );
}

export function usePrepararConcursosFolha() {
    return useIntegracaoMutation(
        ({anoMes, idConcursoPublico}) =>
            api.post(
                withQueryParams(
                    `${INTEGRACAO_ATOS_PESSOAL_URL}/concursos/preparar/${anoMes}`,
                    {idConcursoPublico}
                )
            ),
        "Concursos registrados na fila manual com sucesso"
    );
}

export function usePrepararCargosConcursoFolha() {
    return useIntegracaoMutation(
        ({anoMes, idConcursoPublico}) =>
            api.post(
                withQueryParams(
                    `${INTEGRACAO_ATOS_PESSOAL_URL}/cargos-concurso/preparar/${anoMes}`,
                    {idConcursoPublico}
                )
            ),
        "Cargos do concurso preparados para revisão"
    );
}

export function usePrepararComplementoCargosFolha() {
    return useIntegracaoMutation(
        (anoMes) =>
            api.post(
                `${INTEGRACAO_ATOS_PESSOAL_URL}/cargos/complementar/${anoMes}`
            ),
        "Complementos institucionais dos cargos preparados para revisão"
    );
}

export function usePrepararCargaInicialCargosFolha() {
    return useIntegracaoMutation(
        (anoMes) =>
            api.post(
                `${INTEGRACAO_ATOS_PESSOAL_URL}/cargos/carga-inicial/preparar/${anoMes}`
            ),
        "Carga cadastral inicial de cargos preparada para revisão"
    );
}

export function usePrepararComponentesFolha() {
    return useIntegracaoMutation(
        (anoMes) =>
            api.post(
                `${INTEGRACAO_ATOS_PESSOAL_URL}/componentes-folha/preparar/${anoMes}`
            ),
        "Reconciliação dos componentes de folha concluída"
    );
}

export function usePrepararServidoresAtivosFolha() {
    return useIntegracaoMutation(
        (anoMes) =>
            api.post(
                `${INTEGRACAO_ATOS_PESSOAL_URL}/servidores-ativos/preparar/${anoMes}`
            ),
        "Fotografia dos servidores ativos preparada para revisão"
    );
}

export function usePrepararDadosFuncionaisAtivosFolha() {
    return useIntegracaoMutation(
        (escopo) =>
            api.post(
                typeof escopo === "string"
                    ? `${INTEGRACAO_ATOS_PESSOAL_URL}/dados-funcionais-ativos/preparar/${escopo}`
                    : `${INTEGRACAO_ATOS_PESSOAL_URL}/dados-funcionais-ativos/preparar-categoria/${escopo.anoMes}/${escopo.categoriaFolha}`
            ),
        "Dados funcionais dos vínculos ativos validados para revisão"
    );
}

export function usePrepararVinculosPrevidenciariosFolha() {
    return useIntegracaoMutation(
        (anoMes) =>
            api.post(
                `${INTEGRACAO_ATOS_PESSOAL_URL}/vinculos-previdenciarios/preparar/${anoMes}`
            ),
        "Inativos e pensionistas validados para revisão"
    );
}

export function usePrepararFolhaPagamento() {
    return useIntegracaoMutation(
        ({
            anoMes,
            seqFolhaPagamento,
            tipoFolha,
            categoriaFolha,
            tipoServidor,
            anoMesCompetenciaFolhaSuplementar,
            codigoUnidadeOrcamentariaPadrao
        }) =>
            api.post(
                withQueryParams(
                    `${INTEGRACAO_ATOS_PESSOAL_URL}/folha-pagamento/preparar/${anoMes}`,
                    {
                        seqFolhaPagamento,
                        tipoFolha,
                        categoriaFolha,
                        tipoServidor,
                        anoMesCompetenciaFolhaSuplementar,
                        codigoUnidadeOrcamentariaPadrao
                    }
                )
            ),
        "Folha de pagamento registrada na fila manual com sucesso"
    );
}

export function usePrepararConjuntoFolhaNormal() {
    return useIntegracaoMutation(
        ({anoMes, seqFolhaPagamento, codigoUnidadeOrcamentariaPadrao}) =>
            api.post(
                withQueryParams(
                    `${INTEGRACAO_ATOS_PESSOAL_URL}/folha-pagamento/preparar-normal/${anoMes}`,
                    {
                        seqFolhaPagamento,
                        codigoUnidadeOrcamentariaPadrao
                    }
                )
            ),
        "Conjunto da folha normal preparado por grupo de beneficiários"
    );
}

export function useCancelarHomologacaoAtosPessoal() {
    return useIntegracaoMutation(
        (payload) =>
            api.post(
                `${INTEGRACAO_ATOS_PESSOAL_URL}/cancelar-homologacao`,
                payload
            ),
        "Cancelamentos preparados para revisão"
    );
}

export function usePrepararQuadroVagas() {
    return useIntegracaoMutation(
        (anoMes) =>
            api.post(`${INTEGRACAO_QUADRO_VAGAS_URL}/preparar/${anoMes}`),
        "Quadro de vagas preparado para revisão"
    );
}

export function usePrepararContasBancarias() {
    return useIntegracaoMutation(
        (payload) =>
            api.post(
                `${INTEGRACAO_FINANCEIRA_URL}/contas-bancarias/preparar`,
                payload
            ),
        "Contas bancárias preparadas e aguardando aprovação"
    );
}

export function useCarregarMockContasBancarias() {
    return useMutation((anoMes) =>
        api.get(`${INTEGRACAO_FINANCEIRA_URL}/contas-bancarias/mock`, {
            params: {anoMes}
        })
    );
}

export function usePrepararGestaoFiscal() {
    return useIntegracaoMutation(
        (payload) =>
            api.post(
                `${INTEGRACAO_FINANCEIRA_URL}/gestao-fiscal/preparar`,
                payload
            ),
        "Gestão fiscal preparada e aguardando aprovação"
    );
}

export function useCarregarMockGestaoFiscal() {
    return useMutation(({ano, bimestre}) =>
        api.get(`${INTEGRACAO_FINANCEIRA_URL}/gestao-fiscal/mock`, {
            params: {ano, bimestre}
        })
    );
}

export function useAutorizarOcorrenciaHomologacao() {
    const queryClient = useQueryClient();

    return useMutation(
        ({
            id,
            moduloOrigem,
            justificativa,
            referenciaRemessaContabilAnterior
        }) =>
            api.post(
                `${
                    moduloOrigem === "FINANCEIRA"
                        ? INTEGRACAO_FINANCEIRA_URL
                        : INTEGRACAO_ATOS_PESSOAL_URL
                }/ocorrencias/${id}/autorizar-homologacao`,
                moduloOrigem === "FINANCEIRA"
                    ? {justificativa, referenciaRemessaContabilAnterior}
                    : {justificativa}
            ),
        {
            onSuccess: () => queryClient.invalidateQueries()
        }
    );
}

export function useArquivar() {
    const queryClient = useQueryClient();
    const {showSuccess} = useFeedback();
    return useMutation(
        (id) => api.post(`api/v1/ocorrencias/${id}/acoes/arquivar`),
        {
            onSuccess: () =>
                queryClient
                    .invalidateQueries()
                    .then(() => showSuccess(`Ocorrencia arquivada`))
        }
    );
}

export function useArquivarFluxo() {
    const queryClient = useQueryClient();
    const {showSuccess} = useFeedback();
    return useMutation(
        (id) => api.post(`api/v1/ocorrencias/${id}/acoes/arquivar-fluxo`),
        {
            onSuccess: (response) =>
                queryClient.invalidateQueries().then(() => {
                    const quantidade = response?.data;
                    showSuccess(
                        Number.isFinite(quantidade)
                            ? `Fluxo arquivado com ${quantidade} ocorrências vinculadas`
                            : "Fluxo arquivado"
                    );
                })
        }
    );
}

export function useArquivarProcessadasComErro() {
    const queryClient = useQueryClient();
    const {showSuccess, showError} = useFeedback();

    return useMutation(
        () =>
            api.post("api/v1/ocorrencias/acoes/arquivar-processadas-com-erro"),
        {
            onSuccess: (response) =>
                queryClient
                    .invalidateQueries()
                    .then(() =>
                        showSuccess(mensagemArquivamentoEmLote(response.data))
                    ),
            onError: showError
        }
    );
}

export function mensagemArquivamentoEmLote(resultado) {
    const quantidade =
        typeof resultado === "number"
            ? resultado
            : resultado?.quantidadeArquivada ?? resultado?.quantidade;

    if (quantidade === 0) {
        return "Não há ocorrências processadas com erro para arquivar";
    }

    if (quantidade === 1) {
        return "1 ocorrência processada com erro foi arquivada";
    }

    if (Number.isFinite(quantidade)) {
        return `${quantidade} ocorrências processadas com erro foram arquivadas`;
    }

    return "Ocorrências processadas com erro arquivadas";
}

export function useDesarquivar() {
    const queryClient = useQueryClient();
    const {showInfo} = useFeedback();
    return useMutation(
        (id) => api.post(`api/v1/ocorrencias/${id}/acoes/desarquivar`),
        {
            onSuccess: () =>
                queryClient
                    .invalidateQueries()
                    .then(() => showInfo(`Ocorrência desarquivada`))
        }
    );
}

export function useDesarquivarFluxo() {
    const queryClient = useQueryClient();
    const {showInfo} = useFeedback();
    return useMutation(
        (id) => api.post(`api/v1/ocorrencias/${id}/acoes/desarquivar-fluxo`),
        {
            onSuccess: (response) =>
                queryClient.invalidateQueries().then(() => {
                    const quantidade = response?.data;
                    showInfo(
                        Number.isFinite(quantidade)
                            ? `Fluxo desarquivado com ${quantidade} ocorrências vinculadas`
                            : "Fluxo desarquivado"
                    );
                })
        }
    );
}

export function isAnoMesValido(anoMes) {
    const valor = `${anoMes || ""}`;
    if (!/^\d{6}$/.test(valor)) {
        return false;
    }

    const mes = Number(valor.slice(4, 6));
    return mes >= 1 && mes <= 12;
}

export function withQueryParams(url, params) {
    const query = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (item !== undefined && item !== null && item !== "") {
                    query.append(key, item);
                }
            });
        } else if (value !== undefined && value !== null && value !== "") {
            query.append(key, value);
        }
    });

    const queryString = query.toString();
    return queryString ? `${url}?${queryString}` : url;
}

function useIntegracaoMutation(mutationFn, successMessage) {
    const queryClient = useQueryClient();
    const {showSuccess} = useFeedback();

    return useMutation(mutationFn, {
        onSuccess: () =>
            queryClient
                .invalidateQueries()
                .then(() => showSuccess(successMessage))
    });
}

export function useEnviarExclusao() {
    return useMutation((id) =>
        api.post(`api/v1/ocorrencias/${id}/acoes/excluir`)
    );
}

export function useCancelarPorCodigoRegistro() {
    const queryClient = useQueryClient();
    const {showSuccess} = useFeedback();

    return useMutation(
        (ocorrenciaId) =>
            api.post(
                `api/v1/ocorrencias/${ocorrenciaId}/acoes/preparar-cancelamento`,
                {
                    tipo: "CODIGO_REGISTRO",
                    justificativa: "Cancelamento revisado no conector"
                }
            ),
        {
            onSuccess: () =>
                queryClient
                    .invalidateQueries()
                    .then(() =>
                        showSuccess(
                            "Cancelamento por código de registro preparado para revisão"
                        )
                    )
        }
    );
}

export function useCancelarPorChavePacote() {
    const queryClient = useQueryClient();
    const {showSuccess} = useFeedback();

    return useMutation(
        (ocorrenciaId) =>
            api.post(
                `api/v1/ocorrencias/${ocorrenciaId}/acoes/preparar-cancelamento`,
                {tipo: "CHAVE_PACOTE"}
            ),
        {
            onSuccess: ({data}) =>
                queryClient
                    .invalidateQueries()
                    .then(() =>
                        showSuccess(
                            data?.eventoAtual ===
                                "CANCELAR_TRANSMISSAO_POR_CODIGO_REGISTRO"
                                ? "Pacote finalizado detectado. Cancelamento pelo código de registro da finalização preparado para revisão"
                                : "Cancelamento pela chave do pacote aberto preparado para revisão"
                        )
                    )
        }
    );
}

export function useCategoriasFolha(anoMes) {
    return useQuery(
        [
            INTEGRACAO_ATOS_PESSOAL_URL +
                "/folha-pagamento/categorias-disponiveis/" +
                anoMes
        ],
        queryFetcher,
        {enabled: /^\d{4}(0[1-9]|1[0-2])$/.test(anoMes), staleTime: 0}
    );
}

export function usePrepararCompetenciaFolha() {
    return useIntegracaoMutation(
        ({
            anoMes,
            seqFolhaPagamento,
            seqFolhaIndenizatoria,
            codigoUnidadeOrcamentariaPadrao,
            referenciasComplementares = {}
        }) =>
            api.post(
                withQueryParams(
                    INTEGRACAO_ATOS_PESSOAL_URL +
                        "/folha-pagamento/preparar-competencia/" +
                        anoMes,
                    {
                        seqFolhaPagamento,
                        seqFolhaIndenizatoria,
                        codigoUnidadeOrcamentariaPadrao
                    }
                ),
                {referenciasComplementares}
            ),
        "Folhas disponíveis da competência preparadas para seleção"
    );
}
export function useAutorizarCompetenciaFolha() {
    return useIntegracaoMutation(
        ({anoMes, ocorrenciaIds, justificativa}) =>
            api.post(
                INTEGRACAO_ATOS_PESSOAL_URL +
                    "/folha-pagamento/autorizar-competencia/" +
                    anoMes,
                {ocorrenciaIds, justificativa}
            ),
        "Seleção autorizada exclusivamente em homologação"
    );
}

export function usePessoasOcorrencia(id, filtros, options = {}) {
    const url = withQueryParams(
        "api/v1/ocorrencias/" + id + "/pessoas",
        filtros
    );
    return useQuery([url], queryFetcher, {enabled: Boolean(id), ...options});
}

const INGRESSO_ESTATUTARIO_URL = `${INTEGRACAO_ATOS_PESSOAL_URL}/ingressos-estatutarios`;
export function consultarIngressoEstatutario(movimento, referencia) {
    return api.get(
        `${INGRESSO_ESTATUTARIO_URL}/${encodeURIComponent(movimento)}`,
        {params: {referencia}}
    );
}
export function usePrepararIngressoEstatutario() {
    return useIntegracaoMutation(
        ({movimento, ...payload}) =>
            api.post(
                `${INGRESSO_ESTATUTARIO_URL}/${encodeURIComponent(
                    movimento
                )}/preparar`,
                payload
            ),
        "Ingresso estatutário preparado para revisão"
    );
}
export function usePrepararConvocacaoConcurso() {
    return useIntegracaoMutation(
        ({movimento, ...payload}) =>
            api.post(
                `${INGRESSO_ESTATUTARIO_URL}/${encodeURIComponent(
                    movimento
                )}/convocacao/preparar`,
                payload
            ),
        "Convocação preparada para revisão"
    );
}

const ALTERACAO_POSSE_URL = `${INTEGRACAO_ATOS_PESSOAL_URL}/alteracoes-posse`;
export function consultarAlteracaoPosse(historico, referencia) {
    return api.get(`${ALTERACAO_POSSE_URL}/${encodeURIComponent(historico)}`, {
        params: {referencia}
    });
}
export function usePrepararAlteracaoPosse() {
    return useIntegracaoMutation(
        ({historico, referencia}) =>
            api.post(
                `${ALTERACAO_POSSE_URL}/${encodeURIComponent(
                    historico
                )}/preparar`,
                {referencia}
            ),
        "Alteração de posse preparada para revisão"
    );
}
